'use client';

import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { AtSign, RadioTower } from 'lucide-react';
import { useThemeColor } from '@/hooks/useThemeColor';
import { useAuthStore } from '@/stores/useAuthStore';
import { useModalStore } from '@/stores/useModalStore';
import IconAdaptive from '@/components/IconAdaptive';
import DashboardPersonalAvatar from '@/components/features/dashboard/modal/DashboardPersonalAvatar';
import UserAvatar from '@/components/ui/avatar/UserAvatar';

const AVATAR_MIN_SIZE = 48;
const AVATAR_MAX_SIZE = 96;

export default function DashboardProfileUnit() {
    const { scheme } = useThemeColor();
    const { openModal } = useModalStore();
    const userAuth = useAuthStore((s) => s.userAuth);
    const userAvatarUrl = useAuthStore((s) => s.userAvatarUrl);
    const userSpots = useAuthStore((s) => s.userSpots);
    const profileRef = useRef<HTMLDivElement>(null);
    const [avatarSize, setAvatarSize] = useState(AVATAR_MIN_SIZE);

    useEffect(() => {
        const element = profileRef.current;
        if (!element) return;

        const observer = new ResizeObserver(() => {
            setAvatarSize(Math.min(Math.max(element.offsetHeight, AVATAR_MIN_SIZE), AVATAR_MAX_SIZE));
        });
        observer.observe(element);

        return () => observer.disconnect();
    }, []);

    const openAvatarModal = () =>
        openModal({
            id: 'dashboard-personal-avatar',
            component: <DashboardPersonalAvatar />,
            size: { pcWidth: '800px', pcHeight: '500px', mbWidth: '90%', mbHeight: '80%' },
            direction: 'clip',
            overFlowType: 'auto',
            zIndex: 2500,
        });

    return (
        <div className="flex flex-row items-center md:justify-center w-full gap-x-4 px-4 md:px-6 py-3 md:py-5">
            <motion.div
                initial={false}
                whileHover={{ y: 2 }}
                className="shrink-0 cursor-pointer rounded-full"
                onClick={openAvatarModal}
            >
                <UserAvatar
                    avatarUrl={userAvatarUrl ?? undefined}
                    schoomyers_id={userAuth?.schoomyersId !== undefined ? Number(userAuth.schoomyersId) : undefined}
                    size={avatarSize}
                    borderColorClassName="border-color-base"
                />
            </motion.div>

            <div ref={profileRef} className="flex flex-col min-w-0">
                <div className="flex flex-col items-start min-w-0">
                    {(userSpots ?? []).map((spot) => (
                        <div key={spot.hubId} className="flex items-start gap-x-1 max-w-full">
                            <div className="flex items-center justify-center shrink-0 h-4">
                                <IconAdaptive
                                    Icon={RadioTower}
                                    strokeWidth={2.25}
                                    baseSize={12}
                                    breakpoints={{ md: 14 }}
                                    className="text-color-hub-spot-pin"
                                />
                            </div>
                            <div className="font-corp font-bold text-[11px] md:text-xs leading-4 text-color-base break-words">
                                {[spot.organizationName, spot.branchName].filter(Boolean).join(' ')}
                            </div>
                        </div>
                    ))}
                    <div className="font-corp font-bold text-lg md:text-xl text-color-base text-left max-w-full break-words">
                        {userAuth?.nickname}
                    </div>
                </div>

                <div
                    className={`flex items-center justify-between gap-x-3 w-fit rounded-xl mt-2 px-4 py-1.5 ${scheme?.bg} ${scheme?.bgText}`}
                >
                    <div className="font-corp font-bold text-xs tracking-widest whitespace-nowrap">スクーマーズID.</div>
                    <div className="font-cantarell font-bold text-sm leading-none flex items-center gap-1">
                        <IconAdaptive
                            Icon={AtSign}
                            strokeWidth={2.5}
                            baseSize={16}
                            breakpoints={{ md: 16 }}
                            className="text-color-white-base"
                        />
                        {userAuth?.schoomyersId}
                    </div>
                </div>
            </div>
        </div>
    );
}
